/**
 * First-visit market banner, rendered as a blocking modal <dialog>.
 * - Waits for Shopify's Customer Privacy consent banner (if pending) before
 *   deciding whether to show.
 * - Shows once; if the visitor explicitly picks or confirms a market it
 *   never shows again. If dismissed without a choice, stays quiet for `data-quiet-days`
 *   then shows again.
 * - Exposes `theme.marketBannerReady`, a promise sections/pop-up.liquid
 *   (assets/pop-up.js) waits on before starting its own trigger, so the
 *   newsletter pop-up never appears before this banner has been resolved.
 */
(function () {
  const STORAGE_CHOSEN = 'mm-market-chosen';
  const STORAGE_LAST_SHOWN = 'mm-market-banner-last-shown';
  const DAY_MS = 24 * 60 * 60 * 1000;

  window.theme = window.theme || {};

  const banner = document.querySelector('[data-mm-market-banner]');
  if (!banner) {
    window.theme.marketBannerReady = Promise.resolve();
    return;
  }

  let resolveReady;
  window.theme.marketBannerReady = new Promise((resolve) => {
    resolveReady = resolve;
  });

  const quietDays = Number(banner.dataset.quietDays) || 30;

  function isChosen() {
    return localStorage.getItem(STORAGE_CHOSEN) === '1';
  }

  function isWithinQuietPeriod() {
    const lastShown = Number(localStorage.getItem(STORAGE_LAST_SHOWN));
    if (!lastShown) return false;
    return Date.now() - lastShown < quietDays * DAY_MS;
  }

  function markShown() {
    localStorage.setItem(STORAGE_LAST_SHOWN, String(Date.now()));
  }

  function markChosen() {
    localStorage.setItem(STORAGE_CHOSEN, '1');
  }

  function open() {
    if (typeof banner.showModal === 'function') {
      banner.showModal();
    } else {
      banner.setAttribute('open', '');
    }
  }

  function close() {
    if (typeof banner.close === 'function' && banner.open) {
      banner.close();
    } else {
      banner.removeAttribute('open');
    }
  }

  function evaluate() {
    if (isChosen() || isWithinQuietPeriod()) {
      resolveReady();
      return;
    }
    open();
  }

  // Fires for the close button, Esc (native 'cancel' -> close) and the
  // programmatic close(). Anything that is not an explicit market choice
  // counts as a dismissal and starts the quiet period.
  banner.addEventListener('close', () => {
    if (!isChosen()) markShown();
    resolveReady();
  });

  const closeBtn = banner.querySelector('[data-mm-market-banner-dismiss]');
  if (closeBtn) {
    closeBtn.addEventListener('click', close);
  }

  const form = banner.querySelector('form');
  const countryInput = form ? form.querySelector('input[name="country_code"]') : null;
  const confirmBtn = banner.querySelector('[data-mm-market-banner-confirm]');
  const initialCountry = countryInput ? countryInput.value : null;

  if (form && countryInput && confirmBtn) {
    // snippets/localization-form.liquid submits the form itself on 'change'
    // (bubble-phase listener on the <form>). Inside the dialog the visitor
    // must confirm explicitly, so catch 'change' in the capture phase on the
    // dialog (runs before the form's listener), stop it, and only record the
    // new country in the hidden input.
    // assets/custom-select.js writes the picked name into the button's
    // firstElementChild, which in snippets/custom-select-language.liquid is
    // the globe <svg>, not the text span. Elsewhere the page reloads at once
    // so nobody notices; here the dialog stays open, so restore the icon and
    // update the visible label ourselves.
    const selectBtn = form.querySelector('.custom-select__btn');
    const selectIcon = selectBtn ? selectBtn.querySelector('svg') : null;
    const selectLabel = selectBtn ? selectBtn.querySelector('.text-start') : null;
    const selectIconMarkup = selectIcon ? selectIcon.innerHTML : '';

    banner.addEventListener('change', (evt) => {
      if (!form.contains(evt.target)) return;
      evt.stopPropagation();
      const selected = evt.detail && evt.detail.selectedValue
        ? evt.detail.selectedValue
        : evt.target.value;
      if (!selected) return;
      countryInput.value = selected;

      const option = form.querySelector('[role="option"][data-value="' + selected + '"]');
      if (selectIcon) selectIcon.innerHTML = selectIconMarkup;
      if (selectLabel && option) selectLabel.textContent = option.textContent.trim();
    }, true);

    confirmBtn.addEventListener('click', () => {
      markChosen();
      if (countryInput.value && countryInput.value !== initialCountry) {
        // Different market picked: the reload lands on the new country.
        form.submit();
        return;
      }
      // Same market as the current page: nothing to reload.
      close();
    });
  } else if (form) {
    // No confirm button configured: keep the theme's submit-on-change
    // behaviour and just record the choice before the page reloads.
    form.addEventListener('change', () => {
      markChosen();
      resolveReady();
    });
  }

  // Shopify's cookie consent banner must come first. The customerPrivacy API
  // loads asynchronously, so request it explicitly instead of checking
  // whatever happens to be on window.Shopify at script time (that race let
  // the dialog open on top of a consent banner that appeared moments later).
  // shouldShowBanner() can be true while no consent banner is actually
  // rendered (e.g. theme previews, or the banner app not enabled for the
  // region). Give the banner this long to appear before opening anyway.
  const CONSENT_BANNER_GRACE_MS = 4000;
  let evaluated = false;

  function evaluateOnce() {
    if (evaluated) return;
    evaluated = true;
    document.removeEventListener('visitorConsentCollected', evaluateOnce);
    evaluate();
  }

  function start() {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (privacy && typeof privacy.shouldShowBanner === 'function' && privacy.shouldShowBanner()) {
      document.addEventListener('visitorConsentCollected', evaluateOnce);
      setTimeout(() => {
        if (!document.getElementById('shopify-pc__banner')) evaluateOnce();
      }, CONSENT_BANNER_GRACE_MS);
      return;
    }
    evaluateOnce();
  }

  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      () => start()
    );
  } else {
    start();
  }
})();
